/**
 * PermissionGuard - Component to protect routes and UI based on granular permissions
 * 
 * Usage Examples:
 * 
 * // Route protection
 * <PermissionGuard requiredRoute="/upload">
 *   <UploadPage />
 * </PermissionGuard>
 * 
 * // Module access
 * <PermissionGuard requiredModule="albaranes">
 *   <AlbaranesSection />
 * </PermissionGuard>
 * 
 * // Specific permission
 * <PermissionGuard requiredModule="albaranes" requiredAction="create">
 *   <CreateAlbaranButton />
 * </PermissionGuard>
 * 
 * // Multiple modules (user needs access to ANY)
 * <PermissionGuard requiredModules={["albaranes", "partes_trabajo"]}>
 *   <DocumentsPage />
 * </PermissionGuard>
 * 
 * // Multiple permissions (user needs ALL)
 * <PermissionGuard requiredPermissions={[
 *   { module: "albaranes", action: "create" },
 *   { module: "contactos", action: "read" }
 * ]}>
 *   <CreateAlbaranForm />
 * </PermissionGuard>
 * 
 * // Document type specific
 * <PermissionGuard requiredDocumentType="albaran" requiredAction="create">
 *   <CreateAlbaranButton />
 * </PermissionGuard>
 * 
 * // Legacy role check (still supported)
 * <PermissionGuard requiredRole="company_admin">
 *   <AdminPanel />
 * </PermissionGuard>
 */

import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

export function PermissionGuard({ 
  children,
  
  // New granular permission props
  requiredModule = null,
  requiredAction = null,
  requiredModules = null, // Array of modules (user needs access to ANY)
  requiredPermissions = null, // Array of {module, action} (user needs ALL)
  requiredRoute = null,
  requiredDocumentType = null,
  
  // Legacy props (still supported)
  requiredRole = null,
  requiredPermission = null,
  
  // Behavior props
  fallback = null,
  redirectTo = '/unauthorized',
  showLoading = true,
  loadingComponent = null
}) {
  const { loading: authLoading, isAuthenticated } = useAuth();
  const { 
    loading: permissionsLoading,
    hasPermission,
    hasModuleAccess,
    hasAnyPermission,
    hasAllPermissions,
    canAccessRoute,
    canAccessDocumentType,
    canCreateDocumentType,
    hasRole,
    can // Legacy function
  } = usePermissions();

  const loading = authLoading || permissionsLoading;

  // Show loading state
  if (loading && showLoading) {
    if (loadingComponent) {
      return loadingComponent;
    }
    
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verificando permisos...</p>
        </div>
      </div>
    );
  }

  // Check authentication
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check route access
  if (requiredRoute) {
    if (!canAccessRoute(requiredRoute)) {
      return fallback || <Navigate to={redirectTo} replace />;
    }
  }

  // Check document type access
  if (requiredDocumentType) {
    if (requiredAction === 'create') {
      if (!canCreateDocumentType(requiredDocumentType)) {
        return fallback || <Navigate to={redirectTo} replace />;
      }
    } else {
      if (!canAccessDocumentType(requiredDocumentType)) {
        return fallback || <Navigate to={redirectTo} replace />;
      }
    }
  }

  // Check specific module and action
  if (requiredModule && requiredAction) {
    if (!hasPermission(requiredModule, requiredAction)) {
      return fallback || <Navigate to={redirectTo} replace />;
    }
  }

  // Check module access only
  if (requiredModule && !requiredAction) {
    if (!hasModuleAccess(requiredModule)) {
      return fallback || <Navigate to={redirectTo} replace />;
    }
  }

  // Check multiple modules (user needs access to ANY)
  if (requiredModules && Array.isArray(requiredModules)) {
    const hasAnyModuleAccess = requiredModules.some(module => hasModuleAccess(module));
    if (!hasAnyModuleAccess) {
      return fallback || <Navigate to={redirectTo} replace />;
    }
  }

  // Check multiple permissions (user needs ALL)
  if (requiredPermissions && Array.isArray(requiredPermissions)) {
    if (!hasAllPermissions(requiredPermissions)) {
      return fallback || <Navigate to={redirectTo} replace />;
    }
  }

  // Legacy role check
  if (requiredRole) {
    if (!hasRole(requiredRole)) {
      return fallback || <Navigate to={redirectTo} replace />;
    }
  }

  // Legacy permission check
  if (requiredPermission) {
    if (!can(requiredPermission)) {
      return fallback || <Navigate to={redirectTo} replace />;
    }
  }

  return children;
}

/**
 * Inline permission check component with granular permissions
 */
export function Can({ 
  // New granular props
  module = null,
  action = null,
  modules = null, // Array of modules (user needs access to ANY)
  permissions = null, // Array of {module, action} (user needs ALL)
  route = null,
  documentType = null,
  
  // Legacy props
  do: legacyPermission = null,
  role = null,
  
  children, 
  fallback = null 
}) {
  const { 
    hasPermission,
    hasModuleAccess,
    hasAnyPermission,
    hasAllPermissions,
    canAccessRoute,
    canAccessDocumentType,
    canCreateDocumentType,
    hasRole,
    can // Legacy function
  } = usePermissions();

  // Check route access
  if (route) {
    if (!canAccessRoute(route)) {
      return fallback;
    }
  }

  // Check document type access
  if (documentType) {
    if (action === 'create') {
      if (!canCreateDocumentType(documentType)) {
        return fallback;
      }
    } else {
      if (!canAccessDocumentType(documentType)) {
        return fallback;
      }
    }
  }

  // Check specific module and action
  if (module && action) {
    if (!hasPermission(module, action)) {
      return fallback;
    }
  }

  // Check module access only
  if (module && !action) {
    if (!hasModuleAccess(module)) {
      return fallback;
    }
  }

  // Check multiple modules (user needs access to ANY)
  if (modules && Array.isArray(modules)) {
    const hasAnyModuleAccess = modules.some(mod => hasModuleAccess(mod));
    if (!hasAnyModuleAccess) {
      return fallback;
    }
  }

  // Check multiple permissions (user needs ALL)
  if (permissions && Array.isArray(permissions)) {
    if (!hasAllPermissions(permissions)) {
      return fallback;
    }
  }

  // Legacy role check
  if (role) {
    if (!hasRole(role)) {
      return fallback;
    }
  }

  // Legacy permission check
  if (legacyPermission) {
    if (!can(legacyPermission)) {
      return fallback;
    }
  }

  return children;
}

/**
 * Role-based component (unchanged for compatibility)
 */
export function RoleGuard({ 
  roles = [], 
  children, 
  fallback = null 
}) {
  const { hasRole } = usePermissions();
  
  const hasAnyRole = roles.some(role => hasRole(role));
  
  if (!hasAnyRole) {
    return fallback;
  }
  
  return children;
}

/**
 * Module-based component - shows content only if user has access to module
 */
export function ModuleGuard({
  module,
  action = null,
  children,
  fallback = null
}) {
  const { hasPermission, hasModuleAccess } = usePermissions();
  
  if (action) {
    if (!hasPermission(module, action)) {
      return fallback;
    }
  } else {
    if (!hasModuleAccess(module)) {
      return fallback;
    }
  }
  
  return children;
}

/**
 * Document type guard - shows content only if user can access document type
 */
export function DocumentTypeGuard({
  documentType,
  action = 'read',
  children,
  fallback = null
}) {
  const { canAccessDocumentType, canCreateDocumentType } = usePermissions();
  
  if (action === 'create') {
    if (!canCreateDocumentType(documentType)) {
      return fallback;
    }
  } else {
    if (!canAccessDocumentType(documentType)) {
      return fallback;
    }
  }
  
  return children;
}

/**
 * Navigation guard - shows navigation item only if user should see it
 */
export function NavGuard({
  navItem,
  children,
  fallback = null
}) {
  const { shouldShowNavItem } = usePermissions();
  
  if (!shouldShowNavItem(navItem)) {
    return fallback;
  }
  
  return children;
}

export default PermissionGuard;
